//#region REACT
/* eslint-disable react/jsx-pascal-case */
import { Component } from "react";
import {
  AreaChart,
  BarChart,
  Bar,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import styled from "styled-components";
//#endregion

//#region COMPONENTS
import { NanoFlex } from "../../Elements/NanoFlex";
import Loading from "../../Elements/Loading";
import Type from "../../Elements/Typography";
//#endregion

//#region UTILS
import { withTranslation } from "react-i18next";
//#endregion

//#region STYLED COMPONENTS
const StyledCharts = styled(NanoFlex)`
  margin-left: -5vw;
  margin-right: 2vw;
  .recharts-legend-item-text {
    font-family: "Open Sans";
    font-style: normal;
    font-weight: 400;
    font-size: 13px;
    line-height: 12px;
    letter-spacing: 0.2px;
    color: #252733 !important;
    text-transform: capitalize;
    margin-left: 3px;
  }
  .graph-tooltip {
    background-color: white;
    padding: 5px;
    box-shadow: 0px 5px 18px rgb(40 41 61 / 7%);
    border-radius: 8px;
    p {
      margin: 0;
    }
  }
`;
//#endregion

class Graphs extends Component {
  render() {
    //#region GRAPHS PROPERTIES
    var color =
      this.props.metric === 1
        ? "#2ADD9D"
        : this.props.metric === 2
        ? "#2FBFDE"
        : this.props.metric === 3
        ? "#5e5ce6"
        : this.props.metric === 4
        ? "#FF80AB"
        : this.props.metric === 5
        ? "#FFDA67"
        : this.props.metric === 6
        ? "#8BC34A"
        : "#167FFC";

    var units =
      this.props.metric === 1 ? "º" : this.props.metric === 2 ? "%" : "";

    var formatXAxis = (tickItem) => {
      return new Date(tickItem).toLocaleDateString();
    };

    var legend =
      this.props.days === 7
        ? this.props.t("last7days")
        : this.props.days === 15
        ? this.props.t("last15days")
        : this.props.t("last30days");

    var legend_homologue =
      this.props.days === 7
        ? this.props.t("last15days")
        : this.props.days === 15
        ? this.props.t("last30days")
        : this.props.t("last60days");

    //#endregion

    //#region TOOLTIP
    const CustomTooltip = ({ active, payload, label }) => {
      if (active && payload && payload.length) {
        return (
          <NanoFlex className="graph-tooltip" flexDirection={"column"}>
            <Type.p className="intro">{label}</Type.p>
            {this.props.metric < 5 && (
              <Type.p
                className="label"
                style={{
                  color:
                    this.props.metric === 4
                      ? payload[0].value === 0
                        ? "#DCF5E4"
                        : "#FF92B7"
                      : payload[0].color,
                }}
              >{`${
                this.props.metric === 1 || this.props.metric === 2
                  ? payload[0].name
                  : this.props.metric === 3
                  ? "Gas & Smoke"
                  : this.props.metric === 4
                  ? payload[0].value === 0
                    ? "Activity"
                    : "Inactivity"
                  : ""
              }: ${payload[0].value} ${
                this.props.metric === 1
                  ? "ºC"
                  : this.props.metric === 2
                  ? "%"
                  : ""
              }`}</Type.p>
            )}
            {this.props.metric < 4 && (
              <Type.p className="label" style={{ color: payload[1].color }}>{`${
                this.props.metric === 1 || this.props.metric === 2
                  ? payload[1].name
                  : this.props.metric === 3
                  ? "Co2"
                  : ""
              }: ${payload[1].value} ${
                this.props.metric === 1
                  ? "ºC"
                  : this.props.metric === 2
                  ? "%"
                  : ""
              }`}</Type.p>
            )}
            {this.props.metric > 4 && (
              <Type.p className="label">{`${payload[0].name}: ${payload[0].value}`}</Type.p>
            )}
          </NanoFlex>
        );
      }

      return null;
    };
    //#endregion

    return (
      <StyledCharts alignItems="flex-end">
        {this.props.data?.length > 0 &&
          !this.props.isLoading &&
          this.props.metric !== 4 && (
            // DRAW ALL AREA GRAPHS
            <ResponsiveContainer width="90%" height="85%">
              <AreaChart
                data={this.props.data}
                onClick={(e) => {
                  this.props.onZoomGraph(new Date(e.activeLabel));
                }}
              >
                <CartesianGrid
                  horizontal={true}
                  vertical={false}
                  horizontalFill={
                    //ONLY DRAW TOP AND BOTTOM GRAY ON THE FIRST GRAPH WICH IS TEMPERATURE
                    this.props.metric === 1
                      ? [
                          "#F6F6F6",
                          "#F6F6F6",
                          "#FFFFFF",
                          "#F6F6F6",
                          "#F6F6F6",
                          "#F6F6F6",
                        ]
                      : this.props.metric === 2
                      ? [
                          "#F6F6F6",
                          "#F6F6F6",
                          "#FFFFFF",
                          "#FFFFFF",
                          "#F6F6F6",
                          "#F6F6F6",
                        ]
                      : []
                  }
                  strokeDasharray="4 4"
                />
                <XAxis
                  axisLine={false}
                  tickLine={false}
                  dataKey={"name"}
                  tickFormatter={formatXAxis}
                  tick={{ fontSize: 12 }}
                />
                {/* TEMPERATURE Y AXIS */}
                {this.props.metric === 1 && (
                  <YAxis
                    yAxisId={"left"}
                    axisLine={false}
                    tickLine={false}
                    unit={units}
                    ticks={[0, 9, 18, 23, 26, 34]}
                    tick={{ fontSize: 12 }}
                  />
                )}
                {/* HUMIDITY Y AXIS */}
                {this.props.metric === 2 && (
                  <YAxis
                    yAxisId={"left"}
                    axisLine={false}
                    tickLine={false}
                    unit={units}
                    ticks={[5, 25, 43, 60, 80, 100]}
                    tick={{ fontSize: 12 }}
                  />
                )}
                {/* DEFAULT Y AXIS */}
                {this.props.metric !== 1 && this.props.metric !== 2 && (
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    unit={units}
                    yAxisId={"left"}
                    tick={{ fontSize: 12 }}
                  />
                )}
                {/* ONLY SHOW RIGHT Y AXIS ON GAS OR CO2 */}
                {this.props.metric === 3 && (
                  <YAxis
                    yAxisId={"right"}
                    orientation={"right"}
                    tickLine={false}
                    axisLine={false}
                    tick={{ fontSize: 12 }}
                  />
                )}

                {/* TOOLTIP */}
                <Tooltip
                  labelStyle={{ "font-size": "12px" }}
                  content={<CustomTooltip />}
                />
                {/* ONLY SHOW LEGEND ON DEFINED GRAPHS */}
                {/* GAS AND CO2 */}
                {this.props.metric === 3 && (
                  <Legend
                    verticalAlign={"bottom"}
                    iconType={"circle"}
                    iconSize={8}
                    payload={[
                      {
                        value: "Gas & Smoke",
                        type: "circle",
                        color: "#5e5ce6",
                      },
                      {
                        value: "Co2",
                        type: "circle",
                        color: "#a798ff",
                      },
                    ]}
                  />
                )}
                {/* OTHERS */}
                {this.props.metric < 3 && (
                  <Legend
                    verticalAlign={"bottom"}
                    iconType={"circle"}
                    iconSize={8}
                  />
                )}
                <Area
                  yAxisId="left"
                  type="monotone"
                  dataKey="_value"
                  name={legend}
                  stroke={color}
                  fill={color}                  
                  fillOpacity={0.3}
                />
                {/* CO2 */}
                {this.props.metric === 3 && (
                  <Area
                    yAxisId="right"
                    type="monotone"
                    dataKey="_value_co2"
                    stroke={"#a798ff"}
                    fill={"#a798ff"}                    
                    fillOpacity={0.3}
                  />
                )}
                {/* HOMOLOGUE PERIOD */}
                {(this.props.metric === 1 || this.props.metric === 2) && (
                  <Area
                    yAxisId="left"
                    type="monotone"
                    dataKey="_value_homologue"
                    name={legend_homologue}
                    stroke={"#d5d3d3"}
                    fillOpacity={0}
                  />
                )}
              </AreaChart>
            </ResponsiveContainer>
          )}
        {/* DRAW INACTIVITY GRAPH  */}
        {this.props.data?.length > 0 &&
          !this.props.isLoading &&
          this.props.metric === 4 && (
            <ResponsiveContainer width="90%" height="85%">
              <BarChart data={this.props.data} barGap={-1} barCategoryGap={-1}>
                <XAxis
                  axisLine={false}
                  tickLine={false}
                  dataKey={"name"}
                  tickFormatter={formatXAxis}
                  tick={{ fontSize: 12 }}
                />
                <YAxis tickLine={false} tickCount={2} tick={{ fontSize: 12 }} />
                <Tooltip
                  labelStyle={{ "font-size": "12px" }}
                  content={<CustomTooltip />}
                />
                <Legend
                  verticalAlign={"bottom"}
                  iconSize={8}
                  payload={[
                    {
                      value: this.props.t("inactivity"),
                      type: "circle",
                      color: "#FF92B7",
                    },
                    {
                      value: this.props.t("activity"),
                      type: "circle",
                      color: "#DCF5E4",
                    },
                  ]}
                />
                <Bar
                  dataKey="_value"
                  fill="#FF92B7"
                  background={{ fill: "#DCF5E4" }}
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        {/* SHOW LOADING WHILE WAITING FOR RESPONSE */}
        {this.props.isLoading && <Loading></Loading>}
        {/* SHOW MESSAGE WHEN NO DATA */}
        {this.props.data?.length === 0 && !this.props.isLoading && (
          <NanoFlex>
            <Type.h2>{this.props.t("noResults")}</Type.h2>
          </NanoFlex>
        )}
      </StyledCharts>
    );
  }
}

export default withTranslation()(Graphs);
